import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { listArtworksPage, listContent, thumbUrl, updateContent } from "./artGalleryApi";
import { SITE_URL, formatDate } from "./artGalleryShared";
import { Field, GalleryField, ImageField, Repeater, RichText, StringList, TextField } from "./ArtGalleryFields";
import { CONTENT_SCHEMAS } from "./contentSchemas";

const ArtworkPicker = ({ label, hint, value, onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const data = await listArtworksPage({ q: query || undefined, published: "true", limit: 8, sort: "newest" });
        setResults(data.artworks);
      } catch (error) {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query, open]);

  return (
    <Field label={label} hint={hint}>
      <div className="ag-picker">
        <div className="ag-picker-current">
          {value ? <code>{value}</code> : <span className="ag-muted">Automatic</span>}
          {value && (
            <button type="button" className="ag-link-btn" onClick={() => onChange("")}>
              Clear
            </button>
          )}
          <button type="button" className="ag-link-btn" onClick={() => setOpen((o) => !o)}>
            {open ? "Close" : "Choose"}
          </button>
        </div>
        {open && (
          <div className="ag-picker-panel">
            <input autoFocus placeholder="Search published artworks" value={query} onChange={(e) => setQuery(e.target.value)} />
            <div className="ag-picker-results">
              {results.map((a) => (
                <button
                  type="button"
                  key={a._id}
                  onClick={() => {
                    onChange(a.slug);
                    setOpen(false);
                  }}
                >
                  <img src={thumbUrl(a.croppedImageUrl, 240)} alt="" />
                  <span>
                    {a.title}
                    <small>{a.artist?.name}</small>
                  </span>
                </button>
              ))}
              {!results.length && <span className="ag-muted">No matches</span>}
            </div>
          </div>
        )}
      </div>
    </Field>
  );
};

const renderField = (field, data, setValue, artists) => {
  const value = data[field.key];
  const onChange = (next) => setValue(field.key, next);
  const common = { label: field.label, hint: field.hint, value, onChange };
  switch (field.type) {
    case "textarea":
      return <TextField key={field.key} {...common} multiline rows={3} />;
    case "richtext":
      return <RichText key={field.key} {...common} />;
    case "image":
      return <ImageField key={field.key} {...common} />;
    case "gallery":
      return <GalleryField key={field.key} {...common} />;
    case "strings":
      return <StringList key={field.key} {...common} />;
    case "repeater":
      return <Repeater key={field.key} {...common} fields={field.fields} itemLabel={field.itemLabel} />;
    case "artwork":
      return <ArtworkPicker key={field.key} {...common} />;
    case "artist":
      return (
        <Field key={field.key} label={field.label} hint={field.hint}>
          <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
            <option value="">None</option>
            {artists.map((a) => (
              <option key={a._id} value={a.slug}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
      );
    default:
      return <TextField key={field.key} {...common} />;
  }
};

const ContentPanel = ({ artists, onError }) => {
  const [docs, setDocs] = useState(null);
  const [activeKey, setActiveKey] = useState(CONTENT_SCHEMAS[0].key);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const content = await listContent();
      const byKey = Object.fromEntries(content.map((c) => [c.key, c]));
      setDocs(byKey);
      setDraft(byKey[CONTENT_SCHEMAS[0].key]?.data || {});
    } catch (error) {
      onError(error);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const schema = CONTENT_SCHEMAS.find((s) => s.key === activeKey);
  const savedData = docs?.[activeKey]?.data;
  const dirty = useMemo(() => JSON.stringify(savedData || {}) !== JSON.stringify(draft), [savedData, draft]);

  const switchTo = (key) => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    setActiveKey(key);
    setDraft(docs[key]?.data || {});
  };

  const setValue = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const doc = await updateContent(activeKey, draft);
      setDocs((current) => ({ ...current, [activeKey]: doc }));
      toast.success("Saved - the website will update in a few seconds");
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  if (!docs) return <div className="ag-empty">Loading pages...</div>;

  return (
    <div className="ag-content">
      <nav className="ag-content-nav">
        {CONTENT_SCHEMAS.map((s) => (
          <button key={s.key} className={s.key === activeKey ? "is-active" : ""} onClick={() => switchTo(s.key)}>
            {s.label}
            <small>{docs[s.key]?.updatedAt ? `Updated ${formatDate(docs[s.key].updatedAt)}` : "Not set up"}</small>
          </button>
        ))}
      </nav>
      <div className="ag-content-editor">
        <div className="ag-content-head">
          <div>
            <h2>{schema.label}</h2>
            {schema.description && <p>{schema.description}</p>}
          </div>
          <div className="ag-header-actions">
            <a className="ag-btn ag-btn-ghost" href={`${SITE_URL}${schema.path}`} target="_blank" rel="noreferrer">
              View page ↗
            </a>
            <button className="ag-btn ag-btn-primary" onClick={save} disabled={!dirty || saving}>
              {saving ? "Saving..." : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </div>
        {schema.groups.map((group) => (
          <section className="ag-content-group" key={`${activeKey}-${group.title}`}>
            <h3>{group.title}</h3>
            {group.fields.map((field) => renderField(field, draft, setValue, artists))}
          </section>
        ))}
      </div>
    </div>
  );
};

export default ContentPanel;
